// frontend/src/pages/ThreatRadarPage/ThreatItem.jsx

import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { UIContext } from '../../contexts/UIContext';
import { HistoryContext } from '../../contexts/HistoryContext';
import { AuthContext } from '../../contexts/AuthContext';
import ScoreDonut from '../../pages/VerificationPage/ScoreDonut';
import { getPlatformDisplayName } from '../../components/PlatformUtils/PlatformUtils.jsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import '../../components/HistoryItem/HistoryItem.css'; 

// Icons
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ImageIcon from '@mui/icons-material/Image';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import VideocamIcon from '@mui/icons-material/Videocam';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import AddModeratorIcon from '@mui/icons-material/AddModerator';
import LinkIcon from '@mui/icons-material/Link';
import NotesIcon from '@mui/icons-material/Notes';
import PublicIcon from '@mui/icons-material/Public';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GavelIcon from '@mui/icons-material/Gavel';

import {
    Tooltip, IconButton, Select, MenuItem, FormControl, InputLabel,
    OutlinedInput, Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, CircularProgress, Badge
} from '@mui/material';

const applyBackendEllipsis = (text) => {
    if (!text) return text;
    return `${text}${text.length > 150 - 10 ? '...' : ''}`;
};

const ContentPreview = ({ preview, contentType, metadata }) => {
    const { t } = useTranslation();
    const isValidPreview = (src) => src && (src.startsWith('data:image') || src.startsWith('http'));
    const getTooltipContent = () => {
        const hasAiData = metadata && (metadata.summary || (metadata.safety_flags && metadata.safety_flags.length > 0));
        if (!hasAiData && !preview) return null;

        const categoryText = metadata?.category 
            ? t(`contentMetadata.inclusion.${metadata.category}`, { defaultValue: metadata.category }) 
            : "";
        const contentTypeLabel = t(`contentInput.contentTypes.${contentType}`, { defaultValue: contentType });

        let mediaSection;
        if (contentType === 'text') {
            mediaSection = (
                <div style={{ padding: '1rem', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
                    <p style={{ fontStyle: 'italic', color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.5, fontSize: '0.9rem', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {preview ? applyBackendEllipsis(preview) : t('historyItem.preview.noPreview')}
                    </p>
                </div>
            );
        } else if (contentType === 'audio') {
            mediaSection = (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100px', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '6px', color: 'rgba(255,255,255,0.4)' }}>
                    <AudiotrackIcon style={{ fontSize: '3rem', opacity: 0.7 }} />
                </div>
            );
        } else if ((contentType === 'image' || contentType === 'video') && isValidPreview(preview)) {
           mediaSection = (
                <div style={{ textAlign: 'center', borderRadius: '4px', overflow: 'hidden', backgroundColor: 'rgba(0,0,0,0.2)' }}>
                    <img src={preview} alt="Content preview" style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain' }} />
                </div>
            );
        } else {
             mediaSection = (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80px', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '6px', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', fontStyle: 'italic', textAlign: 'center', padding: '0 1rem' }}>
                    {t('historyItem.preview.noPreview')}
                </div>
            );
        }

        return (
            <div style={{ maxWidth: '400px', fontFamily: 'Roboto, sans-serif', fontSize: '0.875rem' }}>
                <div style={{ marginBottom: '12px' }}>
                    <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.85)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                        {t('verificationPage.contentInfoSection.preview')}
                        <span style={{ color: 'rgba(255, 255, 255, 0.5)', marginLeft: '4px' }}>({contentTypeLabel})</span>
                    </h3>
                    {mediaSection}
                </div>

                {metadata?.summary && (
                    <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '4px', fontWeight: '600', paddingTop: '1rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                            {t('contentMetadata.descriptionLabel')}
                        </div>
                        <div style={{ fontSize: '0.85rem', lineHeight: '1.4', fontStyle: 'italic', color: 'rgba(255,255,255,0.9)' }}>
                            "{metadata.summary}"
                            {categoryText && <span style={{ color: 'var(--secondary-color)', fontWeight: 700, fontStyle: 'normal', marginLeft: '8px' }}>({categoryText})</span>}
                        </div>
                    </div>
                )}

                {metadata?.safety_flags && metadata.safety_flags.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', padding: '10px', backgroundColor: 'rgba(139, 0, 0, 0.2)', border: '1px solid rgba(139, 0, 0, 0.5)', borderRadius: '8px', color: '#F44336' }}>
                        <ReportProblemOutlinedIcon sx={{ fontSize: '1rem', color: '#F44336', marginTop: '2px', flexShrink: 0 }} />
                        <div style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                            <strong style={{ color: '#F44336' }}>{t('metaScores.safetyAlertPrefix')}</strong>{' '}
                            {metadata.safety_flags.map((flag, i) => (
                                <span key={flag}>
                                    {t(`contentMetadata.exclusion.${flag}`, { defaultValue: flag })}
                                    {i < metadata.safety_flags.length - 1 ? ', ' : ''}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const tooltipTitle = getTooltipContent();
    const tooltipProps = {
        tooltip: { sx: { maxWidth: 420, padding: '12px', backgroundColor: '#2D3748', border: '1px solid rgba(255, 255, 255, 0.15)', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)', borderRadius: '4px', fontSize: '0.875rem' } },
        arrow: { sx: { color: '#2D3748' } }
    };

    if (contentType !== 'text') {
        let triggerContent;
        if (contentType === 'audio') {
             triggerContent = <div className="history-item-preview-placeholder"><AudiotrackIcon style={{ fontSize: '2rem', opacity: 0.5 }} /></div>;
        } else if ((contentType === 'image' || contentType === 'video') && isValidPreview(preview)) {
             triggerContent = <img src={preview} alt="Content preview" className="history-item-preview-image" />;
        } else {
             triggerContent = <div className="history-item-preview-placeholder">{t('historyItem.preview.noPreview')}</div>;
        }
        return <Tooltip title={tooltipTitle} arrow placement="right" componentsProps={tooltipProps}>{triggerContent}</Tooltip>;
    }

    if (!preview) return <div className="history-item-preview-placeholder">{t('historyItem.preview.noPreview')}</div>;

    const TRUNCATE_LENGTH = 20;
    const isTruncated = preview.length > TRUNCATE_LENGTH;
    const textToShow = isTruncated ? `${preview.substring(0, TRUNCATE_LENGTH)}...` : preview;

    return (
        <Tooltip title={tooltipTitle} arrow placement="right" componentsProps={tooltipProps}>
            <div className="history-item-preview-placeholder text-preview">
                <p className="history-item-preview-text">{textToShow}</p>
            </div>
        </Tooltip>
    );
};

const calculateLocalVetraScore = (aiScore, fnScore) => {
    if (aiScore == null || fnScore == null) return null;
    const p_ai = aiScore / 100.0;
    const p_fn = fnScore / 100.0;
    const prob_or = 1 - ((1 - p_ai) * (1 - p_fn));
    const synergy_bonus = Math.pow(p_ai * p_fn, 0.5) * 0.15;
    return Math.floor(Math.min(prob_or + synergy_bonus, 1.0) * 100);
};

const getRiskLevel = (score) => {
    if (score == null) return 'minimal';
    if (score >= 90) return 'severe';
    if (score >= 75) return 'high';
    if (score >= 50) return 'moderate';
    if (score >= 25) return 'minor';
    if (score > 10) return 'low';
    return 'minimal';
};

// Main ThreatItem Component
const ThreatItem = ({ item, onShowExplanation, onShowHeatmap, onShowTextInspection, activeScoreFilters, forcedVerificationParams, trackedKeywords }) => {
    const { t, i18n } = useTranslation();
    const { setIsPageBlurred, openDocViewer } = useContext(UIContext);
    const { tasksConfig, masterTaskOrder } = useContext(HistoryContext);
    const { fetchWithAuth, user } = useContext(AuthContext);
    
    const [isContextModalOpen, setIsContextModalOpen] = useState(false);
    const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false);
    const [isHoveringLocked, setIsHoveringLocked] = useState(false);
    const [isUrlCopied, setIsUrlCopied] = useState(false);
    const [copiedKeyword, setCopiedKeyword] = useState(null);
    const [isTakedownModalOpen, setIsTakedownModalOpen] = useState(false);
    const [takedownError, setTakedownError] = useState('');

    const queryClient = useQueryClient();

    const handleCopyKeyword = (e, kw) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(kw);
        setCopiedKeyword(kw);
        setTimeout(() => setCopiedKeyword(null), 2000);
    };

    // Takedown Mutation
    const takedownMutation = useMutation({
        mutationFn: async (payload) => {
            const response = await fetchWithAuth('/api/threat-radar/takedown', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || t('ThreatItem.takedownFailed', 'Failed to submit takedown'));
            return data;
        },
        onSuccess: () => {
            setIsTakedownModalOpen(false);
            queryClient.invalidateQueries(['threats']);
        },
        onError: (err) => {
            setTakedownError(err.message);
        }
    });

    const handleTakedownRequest = () => {
        setTakedownError(''); // Clear any previous errors
        setIsTakedownModalOpen(true);
    };

    const confirmTakedown = () => {
        const sourceUrl = item.original_source_url || (userContext && userContext.source_url);
        if (!sourceUrl) {
            setTakedownError(t('ThreatItem.takedownMissingUrl', 'Cannot request takedown: The public Source URL is missing.'));
            return;
        }
        takedownMutation.mutate({
            analysis_id: activeResult.analysis_id,
            target_url: sourceUrl
        });
    };

    const handleCopyUrl = (e) => {
        e.stopPropagation(); // prevent triggering other clicks
        e.preventDefault();
        if (item.user_context?.source_url) {
            navigator.clipboard.writeText(item.user_context.source_url);
            setIsUrlCopied(true);
            setTimeout(() => setIsUrlCopied(false), 2000); // Reset icon after 2 seconds
        }
    };

    const metadata = useMemo(() => {
        const tasks = item.tasks || {};
        return tasks['ai_detection']?.content_metadata ||
               tasks['fake_news_detection']?.content_metadata ||
               Object.values(tasks).find(t => t?.content_metadata)?.content_metadata || 
               null;
    }, [item.tasks]);

    const hasSafetyFlags = metadata?.safety_flags && metadata.safety_flags.length > 0;

    // --- MATCHED KEYWORDS LOGIC ---
    const matchedInfo = useMemo(() => {
        if (!trackedKeywords || !metadata?.detected_entities) return [];
        const matches = [];
        trackedKeywords.forEach(kw => {
            // Escape special characters in the keyword to prevent regex crashes
            const escapedKw = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            // Use word boundaries (\b) to prevent partial matches like "mus" in "Elon Musk"
            const regex = new RegExp(`\\b${escapedKw}\\b`, 'i');
            
            const foundEntities = metadata.detected_entities.filter(e => regex.test(e));
            if (foundEntities.length > 0) {
                matches.push({ keyword: kw, entities: foundEntities });
            }
        });
        return matches;
    }, [trackedKeywords, metadata]);

    // Build the rich tooltip content for matched keywords
    const matchTooltipContent = matchedInfo.length > 0 ? (
        <div style={{ padding: '4px', minWidth: '220px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <TrackChangesIcon sx={{ fontSize: '1rem', color: '#ffb142' }} />
                {t('ThreatItem.keywordMatches')}
            </div>
            <hr style={{ margin: '6px 0 4px 0', border: 'none', borderTop: '1px solid rgba(255, 255, 255, 0.2)' }} />
            <ul style={{ margin: '0', paddingLeft: '16px' }}>
                {matchedInfo.map((match, idx) => (
                    <li key={idx} style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <strong style={{ color: '#ffb142' }}>{match.keyword}</strong>
                            <div style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.7)', lineHeight: 1.2 }}>
                                {t('ThreatItem.foundIn')}: {match.entities.join(', ')}
                            </div>
                        </div>
                        <Tooltip title={copiedKeyword === match.keyword ? t('ThreatItem.copied', { defaultValue: 'Copied!' }) : t('ThreatItem.copy', { defaultValue: 'Copy Keyword' })} placement="top">
                            <IconButton 
                                size="small" 
                                onClick={(e) => handleCopyKeyword(e, match.keyword)}
                                sx={{ 
                                    color: copiedKeyword === match.keyword ? '#4caf50' : 'white', 
                                    p: 0.5,
                                    backgroundColor: copiedKeyword === match.keyword ? 'rgba(76, 175, 80, 0.1)' : 'transparent'
                                }}
                            >
                                {copiedKeyword === match.keyword ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </li>
                ))}
            </ul>
        </div>
    ) : null;

    const safetyTooltipContent = hasSafetyFlags ? (
        <div style={{ padding: '4px' }}>
            <strong>{t('metaScores.safetyAlertPrefix')}</strong>
            <hr style={{ margin: '6px 0 4px 0', border: 'none', borderTop: '1px solid rgba(255, 255, 255, 0.2)' }} />
            <ul style={{ margin: '0', paddingLeft: '16px' }}>
                {metadata.safety_flags.map(flag => (
                    <li key={flag}>{t(`contentMetadata.exclusion.${flag}`, { defaultValue: flag })}</li>
                ))}
            </ul>
        </div>
    ) : "";

    useEffect(() => {
        if (isContextModalOpen) setIsPageBlurred(true);
        return () => { if (isContextModalOpen) setIsPageBlurred(false); };
    }, [isContextModalOpen, setIsPageBlurred]);

    // Context & URL Logic
    const userContext = item.user_context;
    const hasPublicUrl = !!userContext?.source_url;
    const hasExtraContext = !!(userContext?.user_notes || userContext?.platform);
    const hasContext = hasPublicUrl || hasExtraContext;

    const augmentedTasks = useMemo(() => {
        const raw = { ...(item.tasks || {}) };
        Object.entries(tasksConfig || {}).forEach(([key, config]) => {
            if (config.is_meta_task) {
                const deps = config.depends_on || [];
                if (!raw[key] && deps.length === 2 && raw[deps[0]] && raw[deps[1]]) {
                    const score1 = raw[deps[0]].score;
                    const score2 = raw[deps[1]].score;
                    const metaScore = calculateLocalVetraScore(score1, score2);
                    raw[key] = {
                        score: metaScore,
                        likelihood: metaScore >= 50 ? 'Yes' : 'No',
                        explanation: { 
                            overall_verdict: getRiskLevel(metaScore),
                            contributing_factors: {
                                [deps[0]]: { score: score1, level: getRiskLevel(score1) },
                                [deps[1]]: { score: score2, level: getRiskLevel(score2) }
                            }
                        },
                        is_meta: true
                    };
                } else if (raw[key]) {
                    raw[key].is_meta = true;
                }
            }
        });
        return raw;
    }, [item.tasks, tasksConfig]);

    const tasksToShowInDropdown = useMemo(() => {
        if (!masterTaskOrder) return [];
        return masterTaskOrder.filter(task => augmentedTasks[task] && activeScoreFilters.includes(task));
    }, [activeScoreFilters, masterTaskOrder, augmentedTasks]);

    const [activeTaskKey, setActiveTaskKey] = useState(() => {
        const firstMeta = tasksToShowInDropdown.find(t => tasksConfig?.[t]?.is_meta_task);
        return firstMeta || tasksToShowInDropdown[0] || null;
    });

    const prevTasksRef = useRef(tasksToShowInDropdown);

    const getTaskDisplayName = (taskKey) => {
        return t(`resultDisplay.analysisTasks.${taskKey}`, { defaultValue: tasksConfig?.[taskKey]?.display_name || taskKey });
    };

    useEffect(() => {
        const prevTasks = prevTasksRef.current;
        const arraysEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
        if (!arraysEqual(prevTasks, tasksToShowInDropdown)) {
            prevTasksRef.current = tasksToShowInDropdown;
            const bestAvailableOption = tasksToShowInDropdown.find(t => tasksConfig?.[t]?.is_meta_task) || tasksToShowInDropdown[0] || null;
            if (bestAvailableOption && activeTaskKey !== bestAvailableOption) setActiveTaskKey(bestAvailableOption);
        } else if (!tasksToShowInDropdown.includes(activeTaskKey)) {
            const bestAvailableOption = tasksToShowInDropdown[0] || null;
            if (bestAvailableOption) setActiveTaskKey(bestAvailableOption);
        }
    }, [tasksToShowInDropdown, activeTaskKey, tasksConfig]);

    const activeResult = augmentedTasks[activeTaskKey];
    if (!activeResult) return null;

    // --- DYNAMIC LEDGER LOGIC (From HistoryItem) ---
    const isMetaTask = !!tasksConfig?.[activeTaskKey]?.is_meta_task;
    let ledgerInfo = { hasReceipt: false, versionId: null, logId: null, syncedAt: null, analysisAt: null, tooltipMessage: '', isMetaMismatch: false };

    if (activeResult) {
        if (isMetaTask) {
            const deps = tasksConfig[activeTaskKey].depends_on || [];
            const depResults = deps.map(d => augmentedTasks[d]).filter(Boolean);
            const allHaveReceipts = depResults.length === deps.length && depResults.every(r => r.s3_receipt_version_id);
            const noneHaveReceipts = depResults.every(r => !r.s3_receipt_version_id);
            const allShareSameReceipt = allHaveReceipts && depResults.every(r => r.s3_receipt_version_id === depResults[0].s3_receipt_version_id);

            if (allShareSameReceipt) {
                ledgerInfo = { hasReceipt: true, versionId: depResults[0].s3_receipt_version_id, logId: depResults[0].log_id, syncedAt: depResults[0].ledger_synced_at, analysisAt: depResults[0].analysis_timestamp };
            } else if (noneHaveReceipts) {
                ledgerInfo.tooltipMessage = t('historyItem.ledger.pendingSync');
            } else {
                ledgerInfo.tooltipMessage = t('historyItem.ledger.metaTaskMismatch');
                ledgerInfo.isMetaMismatch = true;
            }
        } else {
            if (activeResult.s3_receipt_version_id) {
                ledgerInfo = { hasReceipt: true, versionId: activeResult.s3_receipt_version_id, logId: activeResult.log_id, syncedAt: activeResult.ledger_synced_at, analysisAt: activeResult.analysis_timestamp };
            } else {
                ledgerInfo.tooltipMessage = t('historyItem.ledger.pendingSync');
            }
        }
    }

    const hasInspectableRegions =
        (item.content_type === 'image' && activeResult.manipulation_regions && activeResult.manipulation_regions?.length > 0) ||
        (item.content_type === 'text' && activeResult.manipulation_regions && activeResult.manipulation_regions?.length > 0) ||
        (item.content_type === 'video' && activeResult.manipulation_regions && activeResult.manipulation_regions?.boxes?.length > 0);

    const explanation = activeResult?.explanation || "";

    let verificationUrl = `/verify?id=${item.exact_hash}`;
    // Append Attribution (Ref)
    if (user?.public_id) {
        verificationUrl += `&ref=${user.public_id}`;
    }
    // Append Context
    if (item.context_hash) {
        verificationUrl += `&ctx=${item.context_hash}`;
    }

    if (forcedVerificationParams) {
        verificationUrl += forcedVerificationParams;
    }

    const formatTimestamp = (isoString) => {
        if (!isoString) return 'N/A';
        const date = new Date(isoString);
        return date.toLocaleString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    };

    const handleTaskChange = (event) => setActiveTaskKey(event.target.value);

    const handleVerifyClick = async (e) => {
        e.preventDefault();
        try {
            await fetchWithAuth(`/api/authorize-bypass/${item.exact_hash}`, { method: 'POST' });
            window.open(verificationUrl, '_blank', 'noopener,noreferrer');
        } catch (err) {
            console.error("Failed to authorize secure bypass", err);
        }
    };

    const handleDownloadReceipt = async (e, logIdToDownload) => {
        e.preventDefault();
        if (isDownloadingReceipt) return;
        setIsDownloadingReceipt(true);

        try {
            const response = await fetchWithAuth(`/api/v1/ledger/receipt/${logIdToDownload}`);
            if (!response.ok) return;
            let filename = `Vetra_AI_Trust_Receipt_${logIdToDownload}.json`;
            const disposition = response.headers.get('Content-Disposition');
            if (disposition && disposition.indexOf('filename=') !== -1) {
                const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
                if (matches != null && matches[1]) { filename = matches[1].replace(/['"]/g, ''); }
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => window.URL.revokeObjectURL(url), 10000);
        } catch (err) {
            console.error("Error downloading receipt:", err);
        } finally {
            setIsDownloadingReceipt(false);
        }
    };

    // --- Dynamic Target Display Name (Truncated like HistoryItem) ---
    const FILENAME_TRUNCATE_LENGTH = 25;
    let rawFilename = t('ThreatItem.localUpload');

    if (item.source_filename) {
        // Strip the backend hash injection to get the clean original name
        rawFilename = item.source_filename.replace(/-[a-f0-9]{6}(\.[a-z0-9]+)$/i, '$1');
        
        // Match "Scraped <Platform> Post" and translate dynamically
        const scrapedMatch = rawFilename.match(/^Scraped\s+(\w+)\s+Post$/i);
        if (scrapedMatch) {
            const platformName = scrapedMatch[1];
            
            const translatedPlatform = getPlatformDisplayName(platformName, t);
            
            rawFilename = t('ThreatItem.scrapedPost', { 
                platform: translatedPlatform, 
                defaultValue: `${t('ThreatItem.scraped', {defaultValue: 'Scraped'})} ${translatedPlatform} ${t('ThreatItem.post', {defaultValue: 'Post'})}` 
            });
        }
    } else if (hasPublicUrl) {
        try {
            const urlObj = new URL(userContext.source_url);
            const pathname = urlObj.pathname;
            const filename = pathname.substring(pathname.lastIndexOf('/') + 1);
            rawFilename = filename ? decodeURIComponent(filename) : urlObj.hostname.replace('www.', '');
        } catch (e) {
            rawFilename = t('ThreatItem.directLink');
        }
    } else {
        rawFilename = t('historyItem.preview.analyzedType', { contentType: item.content_type });
    }

    let displayTargetName = rawFilename;
    const lastDotIndex = rawFilename.lastIndexOf('.');
    if (lastDotIndex > 0 && rawFilename.substring(0, lastDotIndex).length > FILENAME_TRUNCATE_LENGTH) {
        const basename = rawFilename.substring(0, lastDotIndex);
        const extension = rawFilename.substring(lastDotIndex);
        displayTargetName = basename.substring(0, FILENAME_TRUNCATE_LENGTH) + '... ' + extension;
    } else if (lastDotIndex === -1 && rawFilename.length > FILENAME_TRUNCATE_LENGTH) {
        displayTargetName = rawFilename.substring(0, FILENAME_TRUNCATE_LENGTH) + '... ';
    }

    const labelId = `analysis-type-label-${item.exact_hash}`;
    const selectId = `analysis-type-select-${item.exact_hash}`;

    return (
        <div className="history-item-card" style={{ borderColor: 'rgba(255, 177, 66, 0.4)' }}>
            <Tooltip title={t('ThreatItem.viewVerification')} arrow PopperProps={{ style: { zIndex: 9999 } }}>
                <IconButton onClick={handleVerifyClick} className="verification-btn" aria-label={t('ThreatItem.viewVerificationAria')}>
                    <img src="/static/vetraai_logo_grey.webp" alt="Verification Shield" className="shield-icon-image-history" />
                </IconButton>
            </Tooltip>

            <div className="history-item-main-info">
                <div className="history-item-preview-link">
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                        <ContentPreview preview={item.content_preview} contentType={item.content_type} metadata={metadata} />
                        <div className="type-badge-mini" aria-hidden>
                            {item.content_type === 'video' && <VideocamIcon fontSize="inherit" />}
                            {item.content_type === 'image' && <ImageIcon fontSize="inherit" />}
                            {item.content_type === 'audio' && <AudiotrackIcon fontSize="inherit" />}
                            {item.content_type === 'text' && <TextFieldsIcon fontSize="inherit" />}
                        </div>
                    </div>
                </div>
                <div className="history-item-details">
                    <div className="history-filename-row">
                        <Tooltip 
                            title={rawFilename} 
                            arrow 
                            placement="top" 
                            disableHoverListener={rawFilename === displayTargetName}
                            PopperProps={{ className: 'global-dark-tooltip' }}
                        >
                            <span className="history-item-filename" style={{ cursor: rawFilename !== displayTargetName ? 'help' : 'default' }}>
                                {displayTargetName}
                            </span>
                        </Tooltip>
                        
                        {/* Keyword Match Icon */}
                        {matchedInfo.length > 0 && (
                            <Tooltip interactive title={matchTooltipContent} arrow placement="top" PopperProps={{ className: 'global-dark-tooltip' }}>
                                <IconButton 
                                    size="small" 
                                    className="context-indicator-btn" 
                                    sx={{ 
                                        backgroundColor: 'rgba(255, 177, 66, 0.15)', 
                                        color: '#F44336', 
                                        '&:hover': { backgroundColor: 'rgba(255, 177, 66, 0.25)' } 
                                    }}
                                >
                                    <TrackChangesIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                        )}
                        
                        {/* Public URL Icon */}
                        {hasPublicUrl ? (
                            <Tooltip 
                                interactive
                                title={
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 0.5 }}>
                                        <div style={{ fontWeight: 'bold' }}>{t('ThreatItem.publicSourceUrl', { defaultValue: 'Public Source URL' })}</div>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: 'rgba(0,0,0,0.2)', p: '4px 8px', borderRadius: 1, mt: 0.5 }}>
                                            <span style={{ fontSize: '0.75rem', wordBreak: 'break-all', maxWidth: '250px', opacity: 0.8 }}>
                                                {userContext.source_url}
                                            </span>
                                            <Tooltip title={isUrlCopied ? t('ThreatItem.copied', { defaultValue: 'Copied!' }) : t('ThreatItem.copy', { defaultValue: 'Copy URL' })} placement="top">
                                                <IconButton 
                                                    size="small" 
                                                    onClick={handleCopyUrl} 
                                                    sx={{ 
                                                        color: isUrlCopied ? '#4caf50' : 'white', 
                                                        p: 0.5,
                                                        backgroundColor: isUrlCopied ? 'rgba(76, 175, 80, 0.1)' : 'transparent'
                                                    }}
                                                >
                                                    {isUrlCopied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                } 
                                arrow
                                PopperProps={{ className: 'global-dark-tooltip' }}
                            >
                                <IconButton size="small" onClick={() => window.open(userContext.source_url, '_blank', 'noopener,noreferrer')} className="context-indicator-btn" sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                                    <LinkIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Tooltip title={t('ThreatItem.localUploadUser')} arrow>
                                <span style={{ display: 'inline-flex' }}>
                                    <IconButton size="small" disabled className="context-indicator-btn" sx={{ '&.Mui-disabled': { opacity: 0.5, backgroundColor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.3)' } }}>
                                        <LinkIcon fontSize="inherit" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}

                        {/* Context Icon appears AFTER (to the right) of URL Icon */}
                        {hasExtraContext && (
                            <Tooltip title={t('historyItem.context.viewContext')} arrow>
                                <IconButton size="small" onClick={() => setIsContextModalOpen(true)} className="context-indicator-btn">
                                    <AddModeratorIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>

                    <span className="history-item-timestamp" style={{ marginTop: '8px' }}>
                        {formatTimestamp(item.latest_timestamp)}
                    </span>

                    {/* --- DYNAMIC LEDGER BADGE (Restored) --- */}
                    {ledgerInfo.hasReceipt ? (
                        <div className="ledger-info-row">
                            <Tooltip 
                                interactive
                                title={
                                    <div style={{ padding: '4px' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            {t('historyItem.ledger.securedTitle')}
                                            <Tooltip title={t('historyItem.ledger.learnMore')} arrow placement="top" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                <IconButton 
                                                    size="small" 
                                                    onClick={(e) => { e.stopPropagation(); openDocViewer('reference/immutable_trust_ledger.md'); }}
                                                    sx={{ padding: '2px', color: 'var(--secondary-color)', transition: 'all 0.2s ease-in-out', '&:hover': { color: 'var(--secondary-color)', backgroundColor: 'rgba(79, 195, 247, 0.1)' } }}
                                                >
                                                    <HelpOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                        <div style={{ fontSize: '0.75rem', opacity: 0.9, marginTop: '8px', marginBottom: '4px' }}>
                                            • {t('historyItem.ledger.analysisDate')}: {formatTimestamp(ledgerInfo.analysisAt)}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', opacity: 0.9}}>
                                            • {t('historyItem.ledger.notarizationDate')}: {formatTimestamp(ledgerInfo.syncedAt)}
                                        </div>
                                    </div>
                                } 
                                arrow PopperProps={{ className: 'global-dark-tooltip' }}
                            >
                                <div className="ledger-status-badge-mini">
                                    <VerifiedUserIcon sx={{ fontSize: '0.8rem', mr: 0.3 }} />
                                    {t('historyItem.ledger.verifiable')}
                                </div>
                            </Tooltip>
                            <Tooltip title={t('historyItem.ledger.downloadReceipt')} arrow>
                                <IconButton onClick={(e) => handleDownloadReceipt(e, ledgerInfo.logId)} className="ledger-download-btn-mini" size="small" disabled={isDownloadingReceipt}>
                                    {isDownloadingReceipt ? (
                                        <CircularProgress 
                                            size={16} 
                                            sx={{ color: 'var(--secondary-color)' }} 
                                            aria-label={t('ThreatItem.aria.downloading')}
                                        />
                                    ) : (
                                        <FileDownloadIcon sx={{ fontSize: '1rem' }} />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </div>
                    ) : (
                        <div className="ledger-info-row" style={{ opacity: 0.4 }} onMouseEnter={() => setIsHoveringLocked(true)} onMouseLeave={() => setIsHoveringLocked(false)}>
                            <Tooltip title={ledgerInfo.tooltipMessage} arrow PopperProps={{ className: 'global-dark-tooltip' }}>
                                <div className="ledger-status-badge-mini" style={{ cursor: 'help', filter: 'grayscale(1)' }}>
                                    <VerifiedUserIcon sx={{ fontSize: '0.8rem', mr: 0.3 }} />
                                    {t('historyItem.ledger.verifiable')}
                                </div>
                            </Tooltip>
                        </div>
                    )}

                    {hasSafetyFlags && (
                        <Tooltip title={safetyTooltipContent} arrow placement="top">
                            <span className="safety-alert-badge">
                                <ReportProblemOutlinedIcon />
                                {t('historyItem.safetyAlertBadge')}
                            </span>
                        </Tooltip>
                    )}
                </div>
            </div>

            <div className="history-item-results-section">
                {tasksToShowInDropdown.length > 1 ? (
                    <FormControl size="small" className={`history-task-select-form ${isHoveringLocked && ledgerInfo.isMetaMismatch ? 'highlighted-dropdown' : ''}`}>
                        <InputLabel id={labelId}>{t('historyItem.labels.selectScore')}</InputLabel>
                        <Select
                            labelId={labelId} id={selectId} value={activeTaskKey} onChange={handleTaskChange}
                            className="history-task-select" input={<OutlinedInput label={t('historyItem.labels.selectScore')} />}
                            renderValue={(selected) => (
                                <div className="menu-item-with-icon">
                                    {tasksConfig?.[selected]?.is_meta_task && <img src="/static/vetraai_logo_grey.webp" alt={t('ThreatItem.metaAlt')} className="menu-item-logo-icon" />}
                                    <span>{getTaskDisplayName(selected)}</span>
                                </div>
                            )}
                        >
                            {tasksToShowInDropdown.map(taskKey => (
                                <MenuItem key={taskKey} value={taskKey}>
                                    <div className="menu-item-with-icon">
                                        {tasksConfig?.[taskKey]?.is_meta_task && <img src="/static/vetraai_logo_dark_blue.webp" alt={t('ThreatItem.metaAlt')} className="menu-item-logo-icon" />}
                                        <span>{getTaskDisplayName(taskKey)}</span>
                                    </div>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : (
                    /* Stylized rectangle mimicking the Select input field but static */
                    <Box 
                        className={`history-task-select-form ${isHoveringLocked && ledgerInfo.isMetaMismatch ? 'highlighted-dropdown' : ''}`}
                        sx={{ 
                            height: '40px', // Exactly matches the height of the size="small" Select
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: '0 14px', // Matches standard MUI OutlinedInput padding
                            borderRadius: '4px', 
                            border: '1px solid rgba(255, 255, 255, 0.23)', // Matches standard input border
                            boxSizing: 'border-box'
                        }}
                    >
                        <div className="menu-item-with-icon">
                            {tasksConfig?.[activeTaskKey]?.is_meta_task && (
                                <img src="/static/vetraai_logo_grey.webp" alt={t('ThreatItem.metaAlt')} className="menu-item-logo-icon" />
                            )}
                            <span style={{ fontSize: '1rem', color: 'white', letterSpacing: '0.00938em' }}>
                                {getTaskDisplayName(activeTaskKey)}
                            </span>
                        </div>
                    </Box>
                )}

                <div className="history-item-result-display">

                    {/* TAKEDOWN BUTTON */}
                    {(() => {
                        const isSent = activeResult?.takedown_status === 'sent';
                        const isPending = takedownMutation.isPending;
                        const isMissingReceipt = !ledgerInfo.hasReceipt;
                        const isDisabled = isSent || isPending || isMissingReceipt;

                        let tooltipTitle = t('historyItem.actions.requestTakedown', 'Request Legal Takedown');
                        if (isSent) {
                            tooltipTitle = t('historyItem.actions.takedownSent', 'Takedown Dispatched');
                        } else if (isMissingReceipt) {
                            tooltipTitle = t('historyItem.actions.takedownPending', 'Syncing to Immutable Ledger... (Required for Legal Evidence)');
                        }

                        return (
                            <Tooltip title={tooltipTitle} arrow>
                                <span style={{ 
                                    display: 'inline-block', 
                                    cursor: isDisabled ? 'not-allowed' : 'pointer' 
                                }}>
                                    <IconButton
                                        className="explanation-icon-btn"
                                        disabled={isDisabled}
                                        onClick={handleTakedownRequest}
                                        sx={{ 
                                            color: isSent ? 'rgba(255, 255, 255, 0.3)' : '#F44336', 
                                            '&:hover': { backgroundColor: isSent ? 'transparent' : 'rgba(244, 67, 54, 0.1)' },
                                            '&.Mui-disabled': { color: isSent ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.15)' },
                                            // Ensure the disabled button ignores mouse events so the wrapper span catches the hover/cursor
                                            pointerEvents: isDisabled ? 'none' : 'auto'
                                        }}
                                    >
                                        {isPending ? (
                                            <CircularProgress size={20} color="inherit" />
                                        ) : isSent ? (
                                            <Badge
                                                badgeContent={
                                                    <CheckCircleIcon sx={{ fontSize: 14, color: '#4CAF50', backgroundColor: '#1e293b', borderRadius: '50%' }} />
                                                }
                                                overlap="circular"
                                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                sx={{ 
                                                    '& .MuiBadge-badge': { padding: 0, minWidth: 'auto', height: 'auto' } 
                                                }}
                                            >
                                                <GavelIcon />
                                            </Badge>
                                        ) : (
                                            <GavelIcon />
                                        )}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        );
                    })()}

                    {/* EXPLNATION BUTTON (Restored) */}
                    <Tooltip title={explanation ? t('historyItem.actions.viewExplanation') : t('historyItem.tooltips.explanationNotAvailable')} arrow>
                        <span style={{ display: 'inline-block' }}>
                            <IconButton
                                className="explanation-icon-btn"
                                disabled={!explanation}
                                onClick={() => {
                                    if (!explanation) return;
                                    const isMetaTask = !!tasksConfig?.[activeTaskKey]?.is_meta_task;
                                    onShowExplanation(getTaskDisplayName(activeTaskKey, t), activeResult.explanation, isMetaTask, activeResult.score, activeTaskKey);
                                }}
                                sx={{ '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.15)' } }}
                            >
                                <InfoOutlinedIcon />
                            </IconButton>
                        </span>
                    </Tooltip>

                    {/* INSPECTION BUTTON (Restored) */}
                    {(() => {
                        const isMetaTask = !!tasksConfig?.[activeTaskKey]?.is_meta_task;
                        const wasRegionsEnabled = activeResult?.manipulation_regions !== null && activeResult?.manipulation_regions !== undefined;
                        
                        let inspectTooltipText;
                        if (isMetaTask) {
                            inspectTooltipText = t('historyItem.tooltips.metaTaskRegionsDisabled', { defaultValue: 'Inspectable regions are not available for combined meta-scores.' });
                        } else if (item.content_type === 'audio') {
                            inspectTooltipText = t('table.tooltips.regionsDisabledForAudio', { defaultValue: 'Region detection is not available for audio.' });
                        } else if (!wasRegionsEnabled) {
                            inspectTooltipText = t('verificationPage.tooltips.regionsDisabled');
                        } else if (!hasInspectableRegions) {
                            inspectTooltipText = t('resultDisplay.noRegionsFlagged', { taskName: getTaskDisplayName(activeTaskKey) });
                        } else {
                            inspectTooltipText = t('historyItem.actions.inspectDetails');
                        }

                        return (
                            <Tooltip title={inspectTooltipText} arrow>
                                <span style={{ display: 'inline-block' }}>
                                    <IconButton
                                        className="explanation-icon-btn" 
                                        disabled={!hasInspectableRegions}
                                        onClick={() => {
                                            if (item.content_type === 'image') {
                                                onShowHeatmap(item.content_preview, activeResult.manipulation_regions, getTaskDisplayName(activeTaskKey), activeResult.score);
                                            } else if (item.content_type === 'video') {
                                                const videoNote = t('heatmap.footerNoteVideo', { time: activeResult.manipulation_regions.key_frame_time.toFixed(2) });
                                                onShowHeatmap(activeResult.manipulation_regions.key_frame_thumbnail, activeResult.manipulation_regions.boxes, getTaskDisplayName(activeTaskKey), activeResult.score, videoNote);
                                            } else if (item.content_type === 'text') {
                                                onShowTextInspection(activeResult.manipulation_regions, getTaskDisplayName(activeTaskKey), activeResult.score);
                                            }
                                        }}
                                        sx={{ '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.15)' } }}
                                    >
                                        <SearchIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        );
                    })()}

                    <div className="result-pair">
                        <span className="result-label">{t('historyItem.labels.likelihood')}</span>
                        <div className="result-value likelihood">
                            {activeResult.likelihood === 'Yes' ? <WarningIcon className="icon-yes" /> : <CheckCircleIcon className="icon-no" />}
                            <span>{activeResult.likelihood}</span>
                        </div>
                    </div>
                    <div className="result-pair score-pair" style={{ alignItems: 'center' }}>                       
                        <ScoreDonut riskScore={activeResult.score} scoreType={activeTaskKey} t={t} size={50} compact={true} />
                    </div>
                </div>
            </div>

            {hasContext && (
                <Dialog
                    open={isContextModalOpen} onClose={() => setIsContextModalOpen(false)}
                    PaperProps={{ sx: { backgroundColor: '#ffffff !important', color: '#1e293b !important', minWidth: '400px', borderRadius: '12px', backgroundImage: 'none !important', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)' } }}
                    style={{ zIndex: 1400 }}
                >
                    <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1, color: '#0f172a' }}>
                        <AddModeratorIcon sx={{ color: 'var(--secondary-color)' }} />
                        {t('historyItem.context.modalTitle')}
                    </DialogTitle>
                    <DialogContent sx={{ mt: 2 }}>
                        {userContext.source_url && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, fontWeight: 'bold' }}>
                                    <LinkIcon fontSize="small" /> {t('contentInput.context.sourceUrlLabel')}
                                </Typography>
                                <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace', bgcolor: '#f1f5f9', color: '#334155', p: 1, borderRadius: 1, border: '1px solid #e2e8f0', '& a': { color: '#334155', textDecoration: 'none' }, '& a:hover': { textDecoration: 'underline' } }}>
                                    <a href={userContext.source_url} target="_blank" rel="noopener noreferrer">{userContext.source_url}</a>
                                </Typography>
                            </Box>
                        )}
                        {userContext.platform && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, fontWeight: 'bold' }}>
                                    <PublicIcon fontSize="small" /> {t('contentInput.context.platformLabel')}
                                </Typography>
                                <Typography variant="body2" sx={{ bgcolor: '#f1f5f9', color: '#334155', p: 1, borderRadius: 1, border: '1px solid #e2e8f0' }}>
                                    {getPlatformDisplayName(userContext.platform, t)}
                                </Typography>
                            </Box>
                        )}
                        {userContext.user_notes && (
                            <Box>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, fontWeight: 'bold' }}>
                                    <NotesIcon fontSize="small" /> {t('contentInput.context.userNotesLabel')}
                                </Typography>
                                <Typography variant="body2" sx={{ bgcolor: '#f1f5f9', color: '#334155', p: 1, borderRadius: 1, fontStyle: 'italic', border: '1px solid #e2e8f0' }}>
                                    "{userContext.user_notes}"
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                        <Button onClick={() => setIsContextModalOpen(false)} sx={{ color: '#64748b', '&:hover': { color: '#334155', backgroundColor: '#f1f5f9' } }}>
                            {t('historyItem.context.cancel')}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Takedown Confirmation Modal */}
            <Dialog
                open={isTakedownModalOpen} 
                onClose={() => !takedownMutation.isPending && setIsTakedownModalOpen(false)}
                PaperProps={{ sx: { backgroundColor: '#1e293b', color: '#f8fafc', minWidth: '400px', borderRadius: '12px', backgroundImage: 'none', border: '1px solid rgba(255,255,255,0.1)' } }}
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: 'rgba(0, 0, 0, 0.6)' } } }}
                style={{ zIndex: 1400 }}
            >
                <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', gap: 1, color: '#f8fafc' }}>
                    <GavelIcon sx={{ color: '#F44336' }} />
                    {t('ThreatItem.takedownModalTitle', 'Request Legal Takedown')}
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 2, lineHeight: 1.6 }}>
                        {t('ThreatItem.confirmTakedownText', 'Are you sure you want to initiate a formal DMCA takedown request for this content? This will use your Trust Receipt as legal evidence and notify the hosting provider.')}
                    </Typography>
                    
                    {takedownError && (
                        <Typography variant="body2" sx={{ color: '#F44336', bgcolor: 'rgba(244, 67, 54, 0.1)', p: 1.5, borderRadius: 1, border: '1px solid rgba(244, 67, 54, 0.3)' }}>
                            {takedownError}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <Button 
                        onClick={() => setIsTakedownModalOpen(false)} 
                        disabled={takedownMutation.isPending}
                        sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'none', '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.05)' } }}
                    >
                        {t('ThreatItem.cancel', 'Cancel')}
                    </Button>
                    <Button 
                        onClick={confirmTakedown} 
                        variant="contained" 
                        color="error"
                        disabled={takedownMutation.isPending || !!takedownError}
                        sx={{ ml: 2, textTransform: 'none', fontWeight: 'bold' }}
                    >
                        {takedownMutation.isPending ? <CircularProgress size={24} color="inherit" /> : t('ThreatItem.confirmDispatch', 'Confirm & Dispatch')}
                    </Button>
                </DialogActions>
            </Dialog>
        
        </div>
    );
};

export default ThreatItem;